export const COLOR_CHANNELS = ['primary', 'secondary', 'accent', 'structure', 'lights'] as const;

export type ColorChannel = (typeof COLOR_CHANNELS)[number];

export type AttractionPalette = Record<ColorChannel, number>;

export type PalettePreset<T extends string> = {
  id: T;
  label: string;
  palette: AttractionPalette;
};

export const CAROUSEL_PALETTE_IDS = ['candyStripe', 'skyFair', 'mintParade'] as const;
export const COASTER_PALETTE_IDS = ['classicRed', 'oceanBlue', 'sunsetGold'] as const;

export type CarouselPaletteId = (typeof CAROUSEL_PALETTE_IDS)[number];
export type CoasterPaletteId = (typeof COASTER_PALETTE_IDS)[number];

export const CAROUSEL_PALETTES: Record<CarouselPaletteId, PalettePreset<CarouselPaletteId>> = {
  candyStripe: {
    id: 'candyStripe',
    label: 'Candy',
    palette: {
      primary: 0xe85a5a,
      secondary: 0xfff6e8,
      accent: 0xf2c14e,
      structure: 0xd08a4a,
      lights: 0xfff1b0,
    },
  },
  skyFair: {
    id: 'skyFair',
    label: 'Sky',
    palette: {
      primary: 0x4c8fd9,
      secondary: 0xfff8ee,
      accent: 0xf0b429,
      structure: 0xe0b36a,
      lights: 0xffe9a0,
    },
  },
  mintParade: {
    id: 'mintParade',
    label: 'Mint',
    palette: {
      primary: 0x2fbe8a,
      secondary: 0xfff4dc,
      accent: 0xff7a8a,
      structure: 0xd4a05c,
      lights: 0xfff0c2,
    },
  },
};

export const COASTER_PALETTES: Record<CoasterPaletteId, PalettePreset<CoasterPaletteId>> = {
  classicRed: {
    id: 'classicRed',
    label: 'Classic',
    palette: {
      primary: 0xe23d3d,
      secondary: 0xf7f1e4,
      accent: 0xf0c040,
      structure: 0x8d5a3c,
      lights: 0xffe08a,
    },
  },
  oceanBlue: {
    id: 'oceanBlue',
    label: 'Ocean',
    palette: {
      primary: 0x2f7ed8,
      secondary: 0xf4efe6,
      accent: 0x3ec7b2,
      structure: 0x7a5340,
      lights: 0xfff0b8,
    },
  },
  sunsetGold: {
    id: 'sunsetGold',
    label: 'Sunset',
    palette: {
      primary: 0xf0a020,
      secondary: 0xfff4e0,
      accent: 0xe85d4c,
      structure: 0x6e4a34,
      lights: 0xffe6a0,
    },
  },
};

export type ChannelColorTarget = {
  color: { setHex: (hex: number) => unknown };
  emissive?: { setHex: (hex: number) => unknown };
};

export type ChannelColorMap = Record<ColorChannel, ChannelColorTarget>;

export function applyAttractionPalette(channels: ChannelColorMap, palette: AttractionPalette): void {
  channels.primary.color.setHex(palette.primary);
  channels.secondary.color.setHex(palette.secondary);
  channels.accent.color.setHex(palette.accent);
  channels.structure.color.setHex(palette.structure);
  channels.lights.color.setHex(palette.lights);
  channels.lights.emissive?.setHex(palette.lights);
}

export function isCarouselPaletteId(value: string): value is CarouselPaletteId {
  return (CAROUSEL_PALETTE_IDS as readonly string[]).includes(value);
}

export function isCoasterPaletteId(value: string): value is CoasterPaletteId {
  return (COASTER_PALETTE_IDS as readonly string[]).includes(value);
}
